import React from 'react';
import { Info, FileText, Shield, BadgePercent, BookOpen, Clapperboard, ExternalLink, Mail } from 'lucide-react';
import { ExplorerView } from './ExplorerView';
import { useTranslation } from 'react-i18next';

const links = [
  {
    icon: Clapperboard,
    labelKey: 'nav.showcase',
    labelDefault: 'Showcase',
    href: '/showcase',
  },
  {
    icon: FileText,
    labelKey: 'nav.terms',
    labelDefault: 'Terms of Use',
    href: '/termsofuse',
  },
  {
    icon: Shield,
    labelKey: 'nav.privacy',
    labelDefault: 'Privacy Policy',
    href: '/privacy',
  },
  {
    icon: BadgePercent,
    labelKey: 'nav.plans',
    labelDefault: 'Pricing Plans',
    href: '/plans',
  },
  {
    icon: BookOpen,
    labelKey: 'nav.user_manual',
    labelDefault: 'User Manual',
    href: '/usermanual',
  },
];

export const InformationModule: React.FC = () => {
  const { t } = useTranslation();

  const content = (
    <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {links.map(({ icon: Icon, labelKey, labelDefault, href }, i) => (
            <React.Fragment key={href}>
              {i > 0 && <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />}
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 4px',
                  borderRadius: '6px',
                  color: '#38bdf8',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#7dd3fc';
                  (e.currentTarget.querySelector('.ext-icon') as HTMLElement | null)!.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#38bdf8';
                  (e.currentTarget.querySelector('.ext-icon') as HTMLElement | null)!.style.opacity = '0';
                }}
              >
                <Icon size={16} style={{ flexShrink: 0, opacity: 0.8 }} />
                <span style={{ flex: 1 }}>{t(labelKey, labelDefault)}</span>
                <ExternalLink size={12} className="ext-icon" style={{ flexShrink: 0, opacity: 0, transition: 'opacity 0.2s' }} />
              </a>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          {t('information.contact_us', 'Contact Us')}
        </div>
        <a
          href="mailto:support@choreography.studio"
          style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#38bdf8', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500, transition: 'color 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#7dd3fc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#38bdf8'; }}
        >
          <Mail size={16} style={{ flexShrink: 0, opacity: 0.8 }} />
          support@choreography.studio
        </a>
      </div>

      <div className="glass-panel" style={{ padding: '10px 14px', background: 'rgba(30, 41, 59, 0.5)' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          {t('information.dmca', 'DMCA Takedown Request')}
        </div>
        <a
          href="mailto:dmca@choreography.studio"
          style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#38bdf8', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500, transition: 'color 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#7dd3fc'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#38bdf8'; }}
        >
          <Mail size={16} style={{ flexShrink: 0, opacity: 0.8 }} />
          dmca@choreography.studio
        </a>
      </div>
    </div>
  );

  const titleNode = (
    <div className="explorer-header-title-container">
      <div className="module-icon-box" style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Info size={24} />
      </div>
      <div>
        <h2 style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.02em', color: '#38bdf8' }}>
          {t('nav.information', 'Information')}
        </h2>
        <p className="module-header-subtitle" style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
          {t('information.subtitle', 'Legal, pricing, and documentation')}
        </p>
      </div>
    </div>
  );

  return <ExplorerView title={titleNode} content={content} />;
};
